import { CaseReducer, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { get, post } from 'api/requests';

import { data } from 'store/common';

import { AppThunk, IReducer } from 'store/types';
import { IMedia, IParamsReq } from 'types';
import {
  ApartmentStatusStore,
  IChecked,
  IChecklist,
  IData,
  IStatusHistory,
} from 'pages/ApartmentStatus/types';

const initialState: ApartmentStatusStore = {
  checkbox: {} as IChecked,
  checkList: [],
  data: {} as IData,
  loading: false,
  statusHistory: {} as IStatusHistory,
};

const reset: CaseReducer<ApartmentStatusStore> = () => initialState;

const statusHistory: CaseReducer<ApartmentStatusStore, PayloadAction<IStatusHistory>> = (
  state,
  action,
) => {
  state.statusHistory = action.payload;
};

const setCheckList: CaseReducer<ApartmentStatusStore, PayloadAction<IChecklist[]>> = (
  state,
  action,
) => {
  state.checkList = action.payload;
};

const checkBox: CaseReducer<ApartmentStatusStore, PayloadAction<IChecked>> = (state, action) => {
  state.checkbox = action.payload;
};

const setFurnitureInstalled: CaseReducer<
  ApartmentStatusStore,
  PayloadAction<{ placementId: number; isFurnitureInstalled: boolean }>
> = (state, action) => {
  const { placementId, isFurnitureInstalled } = action.payload;
  state.data.floors = state.data.floors?.map(floor => ({
    ...floor,
    placements: floor.placements.map(placement =>
      placement.placementId === placementId ? { ...placement, isFurnitureInstalled } : placement,
    ),
  }));
};

/* TODO доработать общий в web/src/store/common.ts и использовать вместо этого */
const loading: CaseReducer<ApartmentStatusStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

export const apartmentStatusSlice = createSlice({
  name: 'apartmentStatus',
  initialState,
  reducers: {
    data: data as IReducer<ApartmentStatusStore, IData>,
    statusHistory,
    setCheckList,
    checkBox,
    setFurnitureInstalled,
    loading,
    reset,
  },
});

export const {
  statusHistory: setStatusHistory,
  checkBox: setCheckbox,
  setFurnitureInstalled: setFurnitureInstalledAction,
  loading: setLoading,
  reset: setOvpReset,
  data: setData,
} = apartmentStatusSlice.actions;

const fetchData =
  (search: IParamsReq): AppThunk =>
  dispatch => {
    const { structure, section, ...params } = search;
    dispatch(setLoading(true));

    get<IData>(`/api/standards/structures/${structure}/sections/${section}/placements/scs/`, params)
      .then(({ data }) => {
        dispatch(setData(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getOvp =
  (search: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(fetchData(search));
  };

export const updateStatusOvp =
  (cardId: number, description: string, mediaSks: IMedia[], search: IParamsReq): AppThunk =>
  dispatch =>
    post('/api/issues/', [{ placement: cardId, description, mediaSks }]).then(() => {
      dispatch(fetchData(search));
    });

export const createUskDefects =
  (
    issues: {
      placement: number;
      location: number;
      description: string;
      justification?: string;
      mediaSks: { link: string; kind: string }[];
    }[],
    search: IParamsReq,
    callback?: () => void,
    placementStatus?: string,
  ): AppThunk<Promise<unknown>> =>
  dispatch =>
    post('/api/issues/', issues, {
      params: placementStatus ? { placement_status: placementStatus } : undefined,
    }).then(() => {
      callback && callback();
      dispatch(fetchData(search));
    });

export const updateStatusCard =
  (
    selectStatus: string,
    placement: string | number,
    media: IMedia[],
    search: IParamsReq,
    comment?: string,
    refusal_reasons?: { refusal_reason: number; comment?: string }[],
    callback?: () => void,
  ): AppThunk =>
  dispatch => {
    return post('/api/standards/status-placement/', {
      status: selectStatus,
      placement,
      media,
      comment,
      refusal_reasons,
    }).then(() => {
      callback && callback();
      dispatch(fetchData(search));
    });
  };

// перевод в статус Принята ПК из Не одобрено ОВП при отсутствии открытых дефектов (доступно для ГП)
export const updateStatusToAcceptedByPC =
  (
    placement: string | number,
    media: IMedia[],
    search: IParamsReq,
    callback?: () => void,
  ): AppThunk =>
  dispatch => {
    return post('/api/standards/status-placement/approve/', {
      placement,
      media,
    }).then(() => {
      callback && callback();
      dispatch(fetchData(search));
    });
  };

export const uskPreAcceptApartment =
  (
    placement: string | number,
    media: IMedia[],
    search: IParamsReq,
    callback?: () => void,
  ): AppThunk =>
  dispatch => {
    return post('/api/standards/status-placement/usk-pre-accept/', {
      placement,
      media,
    }).then(() => {
      callback && callback();
      dispatch(fetchData(search));
    });
  };

export const getStatusHistory =
  (cardId: number): AppThunk =>
  dispatch => {
    get<IStatusHistory>(`/api/standards/placements/${cardId}/status_history/`).then(({ data }) => {
      dispatch(setStatusHistory(data));
    });
  };

export const setCheckboxStore =
  (data: IChecked): AppThunk =>
  dispatch => {
    dispatch(setCheckbox(data));
  };

export const resetCheckboxStore = (): AppThunk => dispatch => {
  dispatch(setCheckbox({}));
};
